import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";

export interface DonorMetrics {
  pledge: string;
  received: string;
  utilized: string;
  unspent: string;
  approvedUnpaid: string;
  availableToCommit: string;
  outstandingPledge: string;
}

export interface DonorSummary extends DonorMetrics {
  id: string;
  name: string;
  email: string | null;
  type: string;
  active: boolean;
  fundCount: number;
}

export interface DonorDetail {
  id: string;
  name: string;
  email: string | null;
  type: string;
  note: string | null;
  active: boolean;
  summary: DonorMetrics;
  funds: {
    id: string;
    name: string;
    restricted: boolean;
    metrics: DonorMetrics;
    unitIds: string[];
  }[];
  pledges: {
    id: string;
    fundId: string;
    fundName: string;
    amount: string;
    period: string;
    createdAt: string;
  }[];
  receipts: {
    id: string;
    voucherNo: string;
    source: string;
    receiptDate: string;
    total: string;
    pledgeId: string | null;
  }[];
  expenses: {
    id: string;
    referenceNo: string;
    payee: string;
    expenseDate: string;
    status: string;
    total: string;
    paidAmount: string;
  }[];
}

export function useDonorsQuery(enabled = true) {
  return useQuery({
    queryKey: ["donors"],
    queryFn: () => api.get<{ donors: DonorSummary[] }>("/donors"),
    enabled,
  });
}

export function useDonorQuery(donorId: string | null) {
  return useQuery({
    queryKey: ["donors", donorId],
    queryFn: () => api.get<{ donor: DonorDetail }>(`/donors/${donorId}`),
    enabled: Boolean(donorId),
  });
}

export function useCreateDonorMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      email?: string;
      type: string;
      note?: string;
    }) => api.post<{ donor: { id: string } }>("/donors", body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["donors"] });
    },
  });
}

export function useCreateDonorFundMutation(donorId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      restricted?: boolean;
      unitIds: string[];
    }) => api.post<{ fund: { id: string } }>(`/donors/${donorId}/funds`, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["donors"] });
      await queryClient.invalidateQueries({ queryKey: ["donors", donorId] });
      await queryClient.invalidateQueries({ queryKey: ["reference", "funds"] });
    },
  });
}

export function useCreatePledgeMutation(donorId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { fundId: string; amount: string; period: string }) =>
      api.post(`/donors/${donorId}/pledges`, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["donors"] });
      await queryClient.invalidateQueries({ queryKey: ["donors", donorId] });
    },
  });
}
